import React, { useEffect } from 'react';
import { Task } from '../../types';
import { formatDate, parseDate, diffDays } from '../../utils/dateUtils';
import { hasTransitiveDependency } from '../../utils/cycleDetection';

function addDays(dateStr: string, days: number): string {
  const d = parseDate(dateStr);
  d.setDate(d.getDate() + days);
  return formatDate(d);
}

export interface GanttActiveDrag {
  id: number | string;
  action: 'move' | 'left' | 'right' | 'resize-start' | 'resize-end' | 'progress';
  startX?: number;
  startClientX?: number;
  startScrollLeft?: number;
  startY?: number;
  initialStart?: string;
  initialEnd?: string;
  origStart?: string;
  origEnd?: string;
  origDuration?: number;
  snappedStart?: string;
  snappedEnd?: string;
  anchorOffset?: number;
  hasMoved?: boolean;
  [key: string]: unknown;
}

export interface UseGanttPointerDragEffectParams {
  activeDrag: GanttActiveDrag | null;
  setActiveDrag: React.Dispatch<React.SetStateAction<GanttActiveDrag | null>>;
  linkDrag: { sourceId: number | string; mouseX: number; mouseY: number } | null;
  setLinkDrag: React.Dispatch<React.SetStateAction<{ sourceId: number | string; mouseX: number; mouseY: number } | null>>;
  isPanning: boolean;
  setIsPanning: React.Dispatch<React.SetStateAction<boolean>>;
  setLinkTargetId: React.Dispatch<React.SetStateAction<string | number | null>>;
  setDragOffset: React.Dispatch<React.SetStateAction<number>>;
  activeDragRef: React.MutableRefObject<GanttActiveDrag | null>;
  isPanningRef: React.MutableRefObject<boolean>;
  linkDragRef: React.MutableRefObject<{ sourceId: number | string; mouseX: number; mouseY: number } | null>;
  linkTargetIdRef: React.MutableRefObject<string | number | null>;
  tasksRef: React.MutableRefObject<Task[]>;
  visibleTasksRef: React.MutableRefObject<Task[]>;
  commitTasksRef: React.MutableRefObject<((tasks: Task[], action?: string, description?: string) => void) | null>;
  dayWidthRef: React.MutableRefObject<number>;
  timelineDatesRef: React.MutableRefObject<Date[]>;
  dateToIndexMapRef: React.MutableRefObject<Record<string, number>>;
  dragOffsetRef: React.MutableRefObject<number>;
  editTaskCallbackRef: React.MutableRefObject<((id: string | number) => void) | null>;
  scrollContainerRef: React.RefObject<HTMLDivElement | null>;
  sidebarListRef: React.MutableRefObject<{ element?: HTMLDivElement | null; [key: string]: unknown } | null>;
  scrollSourceRef: React.MutableRefObject<'sidebar' | 'gantt' | null>;
  listScrollTopRef: React.MutableRefObject<number>;
  activeTaskElementRef: React.MutableRefObject<HTMLDivElement | null>;
  panStartXRef: React.MutableRefObject<number>;
  panStartYRef: React.MutableRefObject<number>;
  panStartScrollLeftRef: React.MutableRefObject<number>;
  panStartScrollTopRef: React.MutableRefObject<number>;
  clearScrollSource: () => void;
}

export function useGanttPointerDragEffect({
  activeDrag,
  setActiveDrag,
  linkDrag,
  setLinkDrag,
  isPanning,
  setIsPanning,
  setLinkTargetId,
  setDragOffset,
  activeDragRef,
  isPanningRef,
  linkDragRef,
  linkTargetIdRef,
  tasksRef,
  visibleTasksRef,
  commitTasksRef,
  dayWidthRef,
  timelineDatesRef,
  dateToIndexMapRef,
  dragOffsetRef,
  editTaskCallbackRef,
  scrollContainerRef,
  sidebarListRef,
  scrollSourceRef,
  listScrollTopRef,
  activeTaskElementRef,
  panStartXRef,
  panStartYRef,
  panStartScrollLeftRef,
  panStartScrollTopRef,
  clearScrollSource,
}: UseGanttPointerDragEffectParams) {
  useEffect(() => {
    if (!activeDrag && !linkDrag && !isPanning) return;

    const onMouseMove = (e: MouseEvent) => {
      const currentActiveDrag = activeDragRef.current;
      const currentIsPanning = isPanningRef.current;
      const currentLinkDrag = linkDragRef.current;

      if (currentActiveDrag) {
        const scrollContainer = scrollContainerRef.current;
        if (!scrollContainer) return;

        const containerRect = scrollContainer.getBoundingClientRect();
        const scrollLeft = scrollContainer.scrollLeft;

        const mouseXInGrid = e.clientX - containerRect.left + scrollLeft;
        const newBarStartPos = mouseXInGrid - (currentActiveDrag.anchorOffset || 0);

        const startIdx = currentActiveDrag.initialStart ? dateToIndexMapRef.current[currentActiveDrag.initialStart] : undefined;
        const initialBarPos = startIdx !== undefined ? startIdx * dayWidthRef.current : 0;

        const dx = newBarStartPos - initialBarPos;

        const currentDayWidth = dayWidthRef.current;
        const safeThreshold = Math.max(2, Math.min(5, currentDayWidth * 0.4));

        if (!currentActiveDrag.hasMoved && Math.abs(dx) > safeThreshold) {
          setActiveDrag((prev) => (prev ? { ...prev, hasMoved: true } : null));
        }

        if (dx !== dragOffsetRef.current) {
          dragOffsetRef.current = dx;
          setDragOffset(dx);
        }
      } else if (currentIsPanning) {
        const grid = scrollContainerRef.current;
        if (grid) {
          const dx = e.clientX - panStartXRef.current;
          const dy = e.clientY - panStartYRef.current;
          const newLeft = panStartScrollLeftRef.current - dx;
          const newTop = Math.max(0, panStartScrollTopRef.current - dy);

          grid.scrollLeft = newLeft;
          grid.scrollTop = newTop;

          if (sidebarListRef.current?.element) {
            scrollSourceRef.current = 'gantt';
            sidebarListRef.current.element.scrollTop = newTop;
            clearScrollSource();
          }

          listScrollTopRef.current = newTop;
        }
        return;
      } else if (currentLinkDrag) {
        const containerRect = scrollContainerRef.current?.getBoundingClientRect();
        const scrollX = scrollContainerRef.current?.scrollLeft ?? 0;
        const currentListScrollTop = listScrollTopRef.current || 0;
        const mouseX = containerRect ? e.clientX - containerRect.left + scrollX : e.clientX;
        const mouseY = containerRect ? e.clientY - containerRect.top + currentListScrollTop : e.clientY;

        setLinkDrag({ ...currentLinkDrag, mouseX, mouseY });

        if (containerRect) {
          const relY = e.clientY - containerRect.top + currentListScrollTop;
          const rowIdx = Math.floor(relY / 48);
          const currentVisibleTasks = visibleTasksRef.current;

          if (rowIdx >= 0 && rowIdx < currentVisibleTasks.length) {
            const targetTask = currentVisibleTasks[rowIdx];
            if (targetTask.id.toString() !== currentLinkDrag.sourceId.toString()) {
              linkTargetIdRef.current = targetTask.id;
              setLinkTargetId(targetTask.id);
            } else {
              linkTargetIdRef.current = null;
              setLinkTargetId(null);
            }
          } else {
            linkTargetIdRef.current = null;
            setLinkTargetId(null);
          }
        }
        return;
      }
    };

    const onMouseUp = () => {
      const cleanup = () => {
        setIsPanning(false);
        setLinkDrag(null);
        setLinkTargetId(null);
        setActiveDrag(null);
        setDragOffset(0);
        dragOffsetRef.current = 0;
        activeTaskElementRef.current = null;
      };

      const currentIsPanning = isPanningRef.current;
      const currentLinkDrag = linkDragRef.current;
      const currentLinkTargetId = linkTargetIdRef.current;
      const currentActiveDrag = activeDragRef.current;

      if (currentIsPanning) {
        setIsPanning(false);
        return;
      }

      if (currentLinkDrag) {
        if (currentLinkTargetId && String(currentLinkTargetId) !== String(currentLinkDrag.sourceId)) {
          if (!hasTransitiveDependency(currentLinkDrag.sourceId, currentLinkTargetId, tasksRef.current)) {
            const newTasks = tasksRef.current.map(t => {
              if (String(t.id) === String(currentLinkTargetId)) {
                const deps = (t.dependencies || []).map(String);
                const sourceIdStr = String(currentLinkDrag.sourceId);

                if (!deps.includes(sourceIdStr)) {
                  return { ...t, dependencies: [...(t.dependencies || []), currentLinkDrag.sourceId] };
                }
              }
              return t;
            });

            if (commitTasksRef.current) {
              commitTasksRef.current(newTasks, 'update', `Abhängigkeit hinzugefügt`);
            }
          }
        }
        setLinkDrag(null);
        setLinkTargetId(null);
        return;
      }

      if (currentActiveDrag) {
        const currentId = currentActiveDrag.id;
        const currentAction = currentActiveDrag.action;
        const currentStart = currentActiveDrag.initialStart;
        const currentEnd = currentActiveDrag.initialEnd;
        const hadMoved = currentActiveDrag.hasMoved;

        const currentDayWidth = dayWidthRef.current;
        const finalOffset = dragOffsetRef.current;
        const slotShift = Math.round(finalOffset / currentDayWidth);

        cleanup();

        if (!hadMoved && slotShift === 0 && currentAction === 'move') {
          if (editTaskCallbackRef.current) {
            editTaskCallbackRef.current(currentId);
          }
          return;
        }

        if (slotShift !== 0 || currentAction !== 'move') {
          const currentTasks = tasksRef.current;
          const currentTimelineDates = timelineDatesRef.current;
          const currentDateToIndexMap = dateToIndexMapRef.current;

          const newTasks = currentTasks.map(t => {
            if (t.id.toString() === currentId.toString()) {
              const startIdx = currentStart ? (currentDateToIndexMap[currentStart] ?? -1) : -1;
              const endIdx = currentEnd ? (currentDateToIndexMap[currentEnd] ?? -1) : -1;
              const totalSlots = currentTimelineDates.length;

              let newStart: string;
              let newEnd: string;

              if (currentStart) {
                const startIdx = currentDateToIndexMap[currentStart] ?? -1;
                if (startIdx !== -1) {
                  const newStartIdx = startIdx + slotShift;
                  if (newStartIdx >= 0 && newStartIdx < totalSlots) {
                    newStart = formatDate(currentTimelineDates[newStartIdx]);
                  } else {
                    newStart = addDays(currentStart, slotShift);
                  }
                } else {
                  newStart = addDays(currentStart, slotShift);
                }
              } else {
                newStart = '';
              }

              if (currentEnd) {
                const endIdx = currentDateToIndexMap[currentEnd] ?? -1;
                if (endIdx !== -1) {
                  const newEndIdx = endIdx + slotShift;
                  if (newEndIdx >= 0 && newEndIdx < totalSlots) {
                    newEnd = formatDate(currentTimelineDates[newEndIdx]);
                  } else {
                    newEnd = addDays(currentEnd, slotShift);
                  }
                } else {
                  newEnd = addDays(currentEnd, slotShift);
                }
              } else {
                newEnd = '';
              }

              const hadOnlyStart = Boolean(t.start && !t.end);
              const hadOnlyEnd = Boolean(!t.start && t.end);

              if (currentAction === 'move') {
                return {
                  ...t,
                  start: hadOnlyEnd ? '' : newStart,
                  end: hadOnlyStart ? (t.end ? newEnd : '') : newEnd,
                  isInlineNew: false,
                };
              } else if (currentAction === 'left') {
                return diffDays(newStart, t.end || newStart) >= 0
                  ? { ...t, start: newStart, isInlineNew: false }
                  : t;
              } else if (currentAction === 'right') {
                return diffDays(t.start || newEnd, newEnd) >= 0
                  ? { ...t, end: newEnd, isInlineNew: false }
                  : t;
              }
            }
            return t;
          });

          if (commitTasksRef.current) {
            commitTasksRef.current(newTasks, 'update', `Aufgabe angepasst`);
          }
        }
      }
    };

    const handleWindowBlur = () => {
      setIsPanning(false);
      setLinkDrag(null);
      setLinkTargetId(null);
      setActiveDrag(null);
      setDragOffset(0);
      dragOffsetRef.current = 0;
    };

    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
    window.addEventListener('blur', handleWindowBlur);
    window.addEventListener('contextmenu', handleWindowBlur);

    return () => {
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
      window.removeEventListener('blur', handleWindowBlur);
      window.removeEventListener('contextmenu', handleWindowBlur);
    };
  }, [
    activeDrag,
    linkDrag,
    isPanning,
    clearScrollSource,
    activeDragRef,
    activeTaskElementRef,
    commitTasksRef,
    dateToIndexMapRef,
    dayWidthRef,
    dragOffsetRef,
    editTaskCallbackRef,
    isPanningRef,
    linkDragRef,
    linkTargetIdRef,
    listScrollTopRef,
    panStartScrollLeftRef,
    panStartScrollTopRef,
    panStartXRef,
    panStartYRef,
    scrollContainerRef,
    scrollSourceRef,
    setActiveDrag,
    setDragOffset,
    setIsPanning,
    setLinkDrag,
    setLinkTargetId,
    sidebarListRef,
    tasksRef,
    timelineDatesRef,
    visibleTasksRef,
  ]);
}
